// Contiene un crash de RENDER dentro del área de contenido, sin llevarse el shell.
//
// ── Por qué hace falta uno aparte del raíz ───────────────────────────────────────────────────────
// El `ErrorBoundary` de `components/shared` es la última red: ocupa `min-h-screen` y reemplaza la
// página entera. Está bien para un fallo que deja la app inservible, pero es demasiado para el caso
// que de verdad ocurre.
//
// Medido en la auditoría del 2026-08-07: cuando el silo devolvió un `ranges` incompleto —el sobre
// bien formado, faltando `topSkus`— una derivación tiró `Cannot read properties of undefined` en
// pleno render. El raíz lo atrapó y **desaparecieron el sidebar y el topbar**: el operador quedó sin
// menú, sin saber en qué pantalla estaba y sin forma de irse a otra. Y el botón «Recargar» volvía a
// pedir la misma respuesta rota, así que era un bucle sin salida.
//
// ── Las dos diferencias que importan ─────────────────────────────────────────────────────────────
// 1. **No toca el shell.** El error vive dentro del contenido; el menú sigue ahí y el operador puede
//    irse a otra pantalla, que es la salida real cuando una se rompe.
// 2. **Reintentar NO recarga la página.** Resetea el boundary y deja que el árbol vuelva a montar.
//    Si además le pasas `resetKeys`, cambiar de filtro o de rango también lo limpia solo — el crash
//    suele venir de UNA combinación de datos, no de la pantalla entera.
import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/kit';

interface Props {
    children: ReactNode;
    /**
     * Cuando cualquiera de estos valores cambia, el boundary se limpia solo.
     *
     * Sirve para el caso típico: el rango «30 días» revienta y los otros dos están sanos. Sin esto,
     * el operador queda atrapado en el error aunque toque otro pill.
     */
    resetKeys?: unknown[];
    /** Qué se rompió, en palabras del usuario. «No se pudo mostrar el análisis de ventas». */
    title?: string;
    /** Para una sección dentro de un panel: menos alto y un texto corto. */
    compact?: boolean;
}

interface State {
    hasError: boolean;
}

export class ScreenErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    override componentDidUpdate(prev: Props): void {
        if (!this.state.hasError) return;
        const before = prev.resetKeys ?? [];
        const now = this.props.resetKeys ?? [];
        const changed =
            before.length !== now.length || now.some((key, i) => !Object.is(key, before[i]));
        if (changed) this.setState({ hasError: false });
    }

    override componentDidCatch(error: Error, info: ErrorInfo): void {
        // El detalle técnico va a Sentry y a la consola, nunca a la pantalla: al operador no le sirve
        // «Cannot read properties of undefined» y solo consigue que reporte una frase que no entiende.
        Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
    }

    private readonly handleRetry = (): void => {
        this.setState({ hasError: false });
    };

    override render(): ReactNode {
        if (!this.state.hasError) return this.props.children;

        const { compact } = this.props;
        return (
            <div
                role="alert"
                className={
                    compact
                        ? 'flex min-h-[140px] flex-col items-center justify-center gap-2 p-4 text-center'
                        : 'flex min-h-[320px] flex-col items-center justify-center gap-3 p-8 text-center'
                }
            >
                <AlertTriangle
                    className={compact ? 'size-6 text-warning' : 'size-8 text-warning'}
                    aria-hidden
                />
                <h2 className="text-[15px] font-semibold">
                    {this.props.title ?? 'No se pudo mostrar esta pantalla'}
                </h2>
                <p className="max-w-md text-[12px] leading-relaxed text-muted-foreground">
                    {compact
                        ? 'Los datos de esta sección llegaron en un formato que no esperábamos. Puedes intentar de nuevo o elegir otra opción.'
                        : 'Los datos llegaron en un formato que no esperábamos. El resto del portal sigue funcionando: puedes intentar de nuevo o irte a otra pantalla desde el menú.'}
                </p>
                <Button size="sm" onClick={this.handleRetry}>
                    Intentar de nuevo
                </Button>
            </div>
        );
    }
}
